"use client";
import { useSelectableLocationDropdown } from "@/app/directories/shop/components/hooks/useSelectableLocationDropdown";
import { useCartItem } from "@/app/hooks/useCartItem";
import { useValidatedHrtIds } from "@/app/hooks/useValidatedHrtIds";
import {
  type NetworkData,
  type TimeSlot,
  fetchTimeSlots,
  getCredentialedProviderNpiIdsByState,
  getNetworkStatus,
  getPracticesByOrgId,
  getProviderGroupToHrtsByHrtIds,
  reserveEhrSlot,
} from "@/lib/actions/actions";
import { InsuranceMode } from "@/lib/schemas/clientSchema";
import { useBoundStore } from "@/lib/stores/globalStore";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import type { OrganizationAssets } from "@frontend/lib/graphql/generated-gql";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { SelectedAppointmentTimeEnum } from "@frontend/lib/stores/slices/bookingSlice";
import type { CartItem } from "@frontend/lib/stores/slices/pricingMapSlice";
import { sendGTMEvent } from "@next/third-parties/google";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addDays,
  addMinutes,
  endOfMonth,
  getMonth,
  parse,
  parseISO,
  startOfDay,
} from "date-fns";
import { format } from "date-fns-tz";
import { ArrowLeft } from "lucide-react";
import { useParams, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBooking } from "../context/BookingContext";
import {
  DEFAULT_BOOKING_LEAD_TIME_DAYS,
  getBookingLeadTimeDays,
  getNext5Days,
} from "../utils";
import { AppointmentDateTimeSelector } from "./AppointmentDateTimeSelector";
import BookingDateTimeCard from "./BookingDateTimeCard";
import { LocationSelector } from "./LocationSelector";
import { OrderSummary } from "./OrderSummary";
import { ProviderScheduler } from "./ProviderScheduler";

type ProviderFilterCandidate = {
  id: number;
  practiceId?: number | null;
  npiId?: string | null;
};

const handleProviderFiltering = <T extends ProviderFilterCandidate>({
  selectableProviders,
  effectivePracticeId,
  priceType,
  inNetworkProviderIdsByPractice,
  initialPracticeID,
  storefrontProviderFilterEnabled,
  consumerStateForCredentialing,
  selectableProviderNpiIds,
  credentialedProviderNpiIds,
}: {
  selectableProviders: T[];
  effectivePracticeId: number | undefined;
  priceType: InsuranceMode;
  inNetworkProviderIdsByPractice: Map<number, Set<number>> | undefined;
  initialPracticeID: number;
  storefrontProviderFilterEnabled: boolean;
  consumerStateForCredentialing: string;
  selectableProviderNpiIds: string[];
  credentialedProviderNpiIds: string[] | undefined;
}) => {
  if (!selectableProviders.length) return [];
  let providers = selectableProviders;

  if (effectivePracticeId) {
    providers = providers.filter(
      (provider) => provider.practiceId === effectivePracticeId,
    );
  }

  if (priceType === InsuranceMode.INSURANCE && inNetworkProviderIdsByPractice) {
    const inNetworkProvidersForPractice = inNetworkProviderIdsByPractice.get(
      effectivePracticeId ?? initialPracticeID,
    );
    if (inNetworkProvidersForPractice) {
      providers = providers.filter((provider) =>
        inNetworkProvidersForPractice.has(provider.id),
      );
    }
  }

  const shouldApplyCredentialingFilter =
    storefrontProviderFilterEnabled &&
    Boolean(consumerStateForCredentialing) &&
    selectableProviderNpiIds.length > 0 &&
    credentialedProviderNpiIds !== undefined;

  if (shouldApplyCredentialingFilter) {
    const credentialedProviderNpiIdSet = new Set(credentialedProviderNpiIds);
    providers = providers.filter(
      (provider) =>
        !!provider.npiId && credentialedProviderNpiIdSet.has(provider.npiId),
    );
  }

  return providers;
};

const Booking = ({ serverData }: { serverData }) => {
  const clientSlug = useParams<{ clientSlug: string }>().clientSlug;
  const router = useRouter();
  const t = useTranslations("Shop");
  const queryClient = useQueryClient();
  const configFlags =
    queryClient.getQueryData<Record<string, boolean>>([
      "configFlags",
      serverData.org.id,
    ]) ?? {};
  const storefrontProviderFilterEnabled = Boolean(
    configFlags.storefront_provider_filter,
  );
  const consumer = useBoundStore.use.consumer();
  const insurances = useBoundStore.use.insurances();
  const isInsuranceValidated = useBoundStore.use.isInsuranceValidated();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const priceMap = useBoundStore.use.pricingMapEntries();
  const setTemporarySlot = useBoundStore.use.setTemporarySlot();
  const clearTemporarySlot = useBoundStore.use.clearTemporarySlot();
  const {
    currentDisplayDates,
    selectedAppointmentTime,
    setLeadTimeDays,
    setCurrentDisplayDates,
    setSelectedDay,
  } = useBooking();
  const startOfToday = startOfDay(new Date());
  // Declared up here (before the lead-time effect that reads it) so it is not
  // referenced before its declaration.
  const experityBooking = serverData.org.id === 24;

  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    parse(
      `${getMonth(addDays(startOfToday, DEFAULT_BOOKING_LEAD_TIME_DAYS)) + 1}`,
      "M",
      new Date(),
    ),
  );

  const [selectedProviderId, setSelectedProviderId] = useState<
    number | undefined
  >(undefined);
  const { initialHrtIds, hasValidHrtIds } = useValidatedHrtIds();

  const { cartItems } = useCartItem(
    initialHrtIds?.map(Number) ?? [],
    serverData.org.id,
    false,
  );
  const { isVirtualVisit, setIsVirtualVisit } = useBooking();

  const cart = cartItems;

  const hrtsInCart = cart.flatMap((item: CartItem) =>
    item.hrts.map((hrt) => hrt),
  );

  const bookableProcedures = cart.filter((cartItem) =>
    cartItem.hrts.some(
      (hrt) =>
        hrt.ehrPrimaryAppointmentTypeId.length > 0 &&
        hrt.isBookableOnStorefront,
    ),
  );

  // Earliest bookable day is normally 2 days out, but an HRT can override this
  // via its displayMetadata.overrideBookingTimeDays (e.g. 1 => bookable 1 day
  // out). Derive it from the HRTs actually being scheduled.
  const leadTimeDays = getBookingLeadTimeDays(
    bookableProcedures.flatMap((procedure) => procedure.hrts),
  );
  const startOfDayAfterTomorrow = addDays(startOfToday, leadTimeDays);

  // Publish the lead time to BookingContext (so consumers like DateStrip's
  // startOfDayAfterTomorrow reflect it) and re-anchor the date carousel to the
  // correct earliest bookable day in the same place. Experity starts from today
  // and owns its own date computation, so it is skipped.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-anchor only when the lead time changes
  useEffect(() => {
    setLeadTimeDays(leadTimeDays);
    if (experityBooking) return;
    const earliestBookableDay = addDays(startOfDay(new Date()), leadTimeDays);
    setCurrentDisplayDates(getNext5Days(earliestBookableDay));
    setSelectedDay(earliestBookableDay);
  }, [leadTimeDays, experityBooking]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (bookableProcedures.length > 0) {
      setIsVirtualVisit(
        bookableProcedures[0].hrts[0].HrtFlags?.some(
          (flag) => flag.isVirtualVisit,
        ) ?? false,
      );
    }
  }, [bookableProcedures]);

  /**
   * Get all of the locations where the HRT is performed based on the hrt
   */
  const { dedupedLocations: locationOptions, isPending: isLocationPending } =
    useSelectableLocationDropdown(hrtsInCart);

  const { data: allLocations } = useQuery({
    queryKey: ["getAllLocations", serverData.org.id],
    queryFn: () => getPracticesByOrgId(serverData.org.id),
  });

  const initialPracticeID =
    locationOptions.length > 0 &&
    !locationOptions.some(
      (x) => Number.parseInt(x.value) === serverData.practiceID,
    )
      ? Number.parseInt(locationOptions[0].value)
      : serverData.practiceID;

  // Organizational default practiceId may not be valid for our cart.
  // If it's not, switch to first valid location in dropdown.
  const { selectedPracticeId, setSelectedPracticeId } = useBooking();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // trigger re-render when initialPracticeID changes
    if (
      locationOptions.length === 1 &&
      initialPracticeID !== selectedPracticeId
    ) {
      // If there is only one location, set the selected practice id to the first location
      setSelectedPracticeId(Number.parseInt(locationOptions[0].value));
    }
  }, [initialPracticeID]);

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<
    TimeSlot | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const priceType = bookableProcedures.some((x) => x.paymentType === "INSURED")
    ? InsuranceMode.INSURANCE
    : InsuranceMode.SELFPAY;
  const consumerStateForCredentialing = (
    consumer.state ??
    insurances.at(0)?.state ??
    ""
  ).toUpperCase();

  // Shape the shared raw network data into a practiceId -> providerIds map.
  // Memoized so React Query doesn't re-run select (and hand downstream a fresh
  // Map) on every render.
  const selectInNetworkProviderIdsByPractice = useCallback(
    (networkData: NetworkData[]) =>
      networkData.reduce((accum, item) => {
        if (item.inNetwork) {
          const currentSet = accum.get(item.practiceId) ?? new Set<number>();
          return accum.set(item.practiceId, currentSet.add(item.providerId));
        }
        return accum;
      }, new Map<number, Set<number>>()),
    [],
  );

  const hrtIds = bookableProcedures.flatMap((cartItem) =>
    cartItem.hrts.map((hrt) => hrt.hrtId),
  );

  const { data: providerGroupToHrtsData } = useQuery({
    queryKey: ["getProviderGroupToHrtsByHrtIds", hrtIds],
    queryFn: () => getProviderGroupToHrtsByHrtIds(hrtIds),
    initialData: { providerGroupToHrts: [] },
    enabled: bookableProcedures.length > 0,
  });

  // Extract providers from provider groups and map them to their practiceIds
  const selectableProviders = useMemo(() => {
    if (!providerGroupToHrtsData) return [];
    const seenProviderKey = new Set<string>();
    return providerGroupToHrtsData.providerGroupToHrts
      .filter((x) => !x.notBookable && x.isActive)
      .flatMap((group) =>
        group.providerGroup.providerToProviderGroup.flatMap((provider) => ({
          ...provider.provider,
          practiceId: group.providerGroup.practiceId,
        })),
      )
      .filter((provider) => {
        // Use composite key (providerId + practiceId) to allow same provider in different practices
        const key = `${provider.id}-${provider.practiceId}`;
        if (seenProviderKey.has(key)) {
          return false;
        }
        seenProviderKey.add(key);
        return true;
      });
  }, [providerGroupToHrtsData]);

  const selectableProviderNpiIds = useMemo(() => {
    const npiIds = selectableProviders
      .map((provider) => provider.npiId)
      .filter((npiId): npiId is string => Boolean(npiId));
    return Array.from(new Set(npiIds));
  }, [selectableProviders]);

  // Use initialPracticeID as fallback when selectedPracticeId is null.
  const effectivePracticeId = selectedPracticeId ?? initialPracticeID;

  // Extract practiceIds from provider groups associated with the HRTIds
  const providerGroupData = useMemo(() => {
    if (!providerGroupToHrtsData) return undefined;
    const practiceIds = new Set<number>();
    for (const group of providerGroupToHrtsData.providerGroupToHrts.filter(
      (x) => !x.notBookable && x.isActive,
    )) {
      if (group.providerGroup.practiceId) {
        practiceIds.add(group.providerGroup.practiceId);
      }
    }
    return Array.from(practiceIds);
  }, [providerGroupToHrtsData]);

  const { data: organizationAssets } = useQuery({
    queryKey: ["getOrganizationAssets", serverData.org.id],
    queryFn: () => getOrganizationAssetsForOrg(serverData.org.id),
    select: (data) => data,
  });

  const networkStatusHrtId = bookableProcedures.at(0)?.hrts?.[0]?.hrtId;

  const { data: inNetworkProviderIdsByPractice } = useQuery({
    // Shares ["orgNetworkStatus", orgId, hrtId, consumerId, memberId] with the
    // product page's EligibilityButton check so React Query dedups them for the
    // same org/patient/HRT. Key members are stable primitives (not the raw
    // consumer/insurances objects) so async coverage fields (status/planName,
    // consumer.id) populating no longer re-keys and re-fires this request. The
    // queryFn returns the raw NetworkData[] the shared cache stores; this call
    // site derives its Map shape via `select`.
    queryKey: [
      "orgNetworkStatus",
      serverData.org.id,
      networkStatusHrtId,
      consumer?.id,
      insurances[0]?.memberId,
    ],
    queryFn: () => {
      if (!networkStatusHrtId) return [];
      return getNetworkStatus(
        consumer,
        insurances,
        serverData.org.id,
        networkStatusHrtId,
      );
    },
    select: selectInNetworkProviderIdsByPractice,
    // Gate on coverage being done — mirror EligibilityButton's product-page
    // gate (isInsuranceValidated && !isPendingPricesMode) so this scheduling
    // check does not fire while coverage is still in-flight and does not
    // re-fire when it resolves.
    enabled:
      bookableProcedures.length > 0 &&
      networkStatusHrtId != null &&
      insurances.length > 0 &&
      isInsuranceValidated &&
      !isPendingPricesMode,
    // Scoped guardrails for the eligibility/network round-trip: coalesce
    // in-flight duplicates for a few minutes and don't auto-refetch on
    // window focus / reconnect. See PR for rationale.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: credentialedProviderNpiIds } = useQuery({
    queryKey: [
      "getCredentialedProviderNpiIdsByState",
      selectableProviderNpiIds,
      consumerStateForCredentialing,
    ],
    queryFn: () =>
      getCredentialedProviderNpiIdsByState(
        selectableProviderNpiIds,
        consumerStateForCredentialing,
      ),
    enabled:
      storefrontProviderFilterEnabled &&
      selectableProviderNpiIds.length > 0 &&
      Boolean(consumerStateForCredentialing),
  });

  // Filter providers by practice, insured in-network status, and state credentialing.
  const filteredProviders = useMemo(() => {
    return handleProviderFiltering({
      selectableProviders,
      effectivePracticeId,
      priceType,
      inNetworkProviderIdsByPractice,
      initialPracticeID,
      storefrontProviderFilterEnabled,
      consumerStateForCredentialing,
      selectableProviderNpiIds,
      credentialedProviderNpiIds,
    });
  }, [
    selectableProviders,
    effectivePracticeId,
    priceType,
    inNetworkProviderIdsByPractice,
    initialPracticeID,
    storefrontProviderFilterEnabled,
    consumerStateForCredentialing,
    selectableProviderNpiIds,
    credentialedProviderNpiIds,
  ]);

  const { data: openSlots, refetch: refetchSlots } = useQuery({
    queryKey: [
      "fetchTimeSlots",
      selectedPracticeId,
      bookableProcedures.at(0)?.hrts[0].hrtId,
      currentDisplayDates,
    ],
    queryFn: () =>
      fetchTimeSlots({
        orgId: serverData.org.id,
        providerId: undefined,
        practiceId: [selectedPracticeId ?? initialPracticeID],
        startDateTime: currentDisplayDates[0].toISOString(),
        endDateTime: currentDisplayDates[4].toISOString(),
        hrtId: bookableProcedures[0].hrts[0].hrtId,
      }),
    select: (timeSlots) => {
      return timeSlots?.map((slot) => {
        const [ehrAppointmentTypeId, ehrAppointmentTypeName] =
          slot.reason.split("|");
        return {
          ...slot,
          practiceId: slot.practiceId ?? selectedPracticeId,
          ehrAppointmentTypeId,
          ehrAppointmentTypeName,
        };
      });
    },
    // should be able to run in parallel with filteredProviders but somehow doesn't.
    // Enforce that filteredProviders returns first because this one is slow.
    enabled: bookableProcedures.length > 0 && filteredProviders.length > 0,
  });

  const {
    isLoading: isCalendarLoading,
    data: openCalendarSlots,
    refetch: refetchCalendarSlots,
  } = useQuery({
    queryKey: [
      "fetchTimeSlotsForCalendar",
      selectedPracticeId,
      bookableProcedures.at(0)?.hrts[0].hrtId,
      calendarMonth,
    ],
    queryFn: () =>
      fetchTimeSlots({
        orgId: serverData.org.id,
        providerId: undefined,
        practiceId: [selectedPracticeId ?? initialPracticeID],
        startDateTime:
          startOfDayAfterTomorrow > calendarMonth
            ? startOfDayAfterTomorrow.toISOString()
            : calendarMonth.toISOString(),
        endDateTime: endOfMonth(calendarMonth).toISOString(),
        hrtId: bookableProcedures[0].hrts[0].hrtId,
      }),
    select: (timeSlots) => {
      const datesSet = new Set();
      for (const timeslot of timeSlots ?? []) {
        const formattedDate = format(new Date(timeslot.dateTime), "yyyy-M-dd");
        if (!datesSet.has(formattedDate)) {
          datesSet.add(formattedDate);
        }
      }
      return datesSet;
    },
    enabled: false,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    refetchCalendarSlots();
  }, [isCalendarOpen, calendarMonth]);

  const refetchThenClearError = async () => {
    await Promise.all([
      refetchSlots(),
      new Promise((resolve) => setTimeout(resolve, 5000)),
    ]);
    setErrorMessage("");
  };

  const selectedProvider = filteredProviders.find(
    (provider) =>
      provider.id === (selectedProviderId ?? selectedTimeSlot?.providerId),
  );

  const outOfNetwork =
    priceType === InsuranceMode.INSURANCE &&
    selectedProvider &&
    inNetworkProviderIdsByPractice &&
    !inNetworkProviderIdsByPractice
      ?.get(selectedPracticeId ?? initialPracticeID)
      ?.has(selectedProvider.id);

  const providerOptions = filteredProviders.map((x) => ({
    label: `${x.firstName} ${x.lastName}`,
    title: x.title ?? undefined,
    degrees: x.degrees ?? undefined,
    value: x.id.toString(),
    img: x.imageUrl ?? null,
  }));

  const handleSubmit = async (timeSlot: TimeSlot) => {
    setIsSubmitting(true);

    if (!timeSlot) {
      setIsSubmitting(false);
      clearTemporarySlot();
      return;
    }

    const res = await reserveEhrSlot(
      timeSlot.ehrAppointmentId,
      serverData.org.id,
    );

    if (!res || res?.error) {
      setErrorMessage(
        "This slot is no longer available. Refreshing available slots...",
      );
      refetchThenClearError();
      clearTemporarySlot();
      setSelectedTimeSlot(undefined);
      setIsSubmitting(false);
      return;
    }

    // If an out of network provider selected, update the cart data and price type
    // FIXME: HANDLE THE OON CASE
    if (outOfNetwork) {
      const selfPayCart = cart.map((x) => ({
        ...x,
        paymentType: "CASH" as "INSURED" | "CASH",
        price: x.hrts.reduce(
          (accum, item) =>
            accum + (priceMap[item.hrtId]?.priceData.CASH?.price ?? 0),
          0,
        ),
      }));
      for (const cartItem of selfPayCart) {
        // addCartItem(cartItem, clientSlug);
      }
    }

    const appointmentEnd = addMinutes(
      parseISO(timeSlot.dateTime),
      timeSlot.duration,
    ).toISOString();

    const provider = filteredProviders.find(
      (provider) => provider.id === timeSlot.providerId,
    );

    if (!timeSlot.ehrAppointmentId.at(0)) {
      setErrorMessage(
        "This slot is no longer available. Refreshing available slots...",
      );
      refetchThenClearError();
      clearTemporarySlot();
      setSelectedTimeSlot(undefined);
      setIsSubmitting(false);
      return;
    }

    const ehrPrimaryAppointmentTypeId =
      bookableProcedures[0].hrts[0].ehrPrimaryAppointmentTypeId[0];

    setTemporarySlot({
      startTime: timeSlot.dateTime,
      endTime: appointmentEnd,
      ehrVisitId: timeSlot.ehrAppointmentId[0],
      providerId: timeSlot.providerId,
      practiceId: timeSlot.practiceId,
      timezone: timeSlot.timezone,
      isVirtualVisit: isVirtualVisit,
      ehrAppointmentTypeId:
        ehrPrimaryAppointmentTypeId ?? timeSlot.ehrAppointmentTypeId,
    });

    sendGTMEvent({
      event: "booking_time_selected",
      provider: `${provider?.firstName} ${provider?.lastName}`,
      date: timeSlot.dateTime,
    });

    router.push(`/${clientSlug}/review?hrtId=${initialHrtIds?.join(",")}`);
  };

  const handleSkipBooking = (
    overrideAppointmentTime?: Date | SelectedAppointmentTimeEnum,
  ) => {
    // In the Experity flow, selecting a specific time slot stores a Date in
    // `selectedAppointmentTime`, whereas ASAP stores SelectedAppointmentTimeEnum.ASAP
    // (and skipping leaves it null). Only a concrete Date means the user chose a time.
    //
    // The ASAP button (AppointmentDateTimeSelector) sets ASAP and calls this in
    // the same event handler, so the render-bound `selectedAppointmentTime` here
    // is still stale (the pre-ASAP value). Callers that mutate the value first
    // must pass it explicitly via `overrideAppointmentTime`; other call sites
    // (wired directly to onClick, receiving the event object) fall back to state.
    const appointmentTime =
      overrideAppointmentTime instanceof Date ||
      overrideAppointmentTime === SelectedAppointmentTimeEnum.ASAP
        ? overrideAppointmentTime
        : selectedAppointmentTime;

    if (appointmentTime instanceof Date) {
      sendGTMEvent({
        event: "booking_time_selected",
        date: appointmentTime.toISOString(),
      });
    } else {
      sendGTMEvent({
        event: "booking_skipped",
      });
    }
    clearTemporarySlot();
    router.push(`/${clientSlug}/review?hrtId=${initialHrtIds?.join(",")}`);
  };

  if (!hasValidHrtIds) return null;

  return (
    <div
      className="grow sm:bg-white flex flex-col justify-center items-center xl:pb-[256px] sm:pb-[128px] relative"
      style={{
        backgroundColor: experityBooking
          ? (organizationAssets?.primaryBackgroundColor ?? "#FAFAFA")
          : "#FAFAFA",
      }}
    >
      <div className="sm:justify-center xl:justify-start relative sm:z-10 xl:z-0 border-none flex xl:w-[70%] sm:w-full xl:pt-16 sm:py-4 xl:pb-[48px]  sm:px-4 xl:px-0">
        <div className="relative sm:w-full">
          <button
            type="button"
            className="absolute sm:left-0 lg:left-[-48px] top-0 rounded-full flex items-center justify-center p-1"
            style={{
              backgroundColor: organizationAssets?.primaryColor ?? "#000000",
            }}
            onClick={() => router.back()}
          >
            <ArrowLeft color={"white"} strokeWidth={1} size={16} />
          </button>
          <p
            style={{
              color: organizationAssets?.primaryColor ?? "#000000",
            }}
            className="text-3xl font-medium text-left pl-8 lg:pl-0"
          >
            {t("ScheduleAppointment")}
          </p>
        </div>
      </div>

      <div className="flex xl:w-[80%] sm:w-full gap-6 sm:flex-col xl:flex-row sm:pt-4">
        <div className="flex flex-col items-stretch gap-8 w-full px-4">
          <LocationSelector
            experityBooking={experityBooking}
            organizationAssets={
              organizationAssets as OrganizationAssets | null | undefined
            }
            locations={
              allLocations
                ?.filter(
                  (practice) =>
                    practice.ehrLocationId !== null &&
                    practice.isAvailableFromStorefront === true &&
                    (providerGroupData === undefined ||
                      providerGroupData.length === 0 ||
                      providerGroupData.includes(practice.practiceId)),
                )
                .map((practice) => ({
                  address: practice.address,
                  label: `${practice.locationName} - ${practice.address}`,
                  value: practice.practiceId.toString(),
                  phoneNumber: practice.phoneNumber,
                  googleMapsEmbed: practice.googleMapsEmbed,
                })) ?? []
            }
            selectedPracticeId={selectedPracticeId ?? initialPracticeID}
            onSelectLocation={setSelectedPracticeId}
          />
          {experityBooking ? (
            selectedPracticeId !== null && (
              <AppointmentDateTimeSelector
                primaryColor={organizationAssets?.primaryColor ?? "#000000"}
                secondaryBackgroundColor={"#D9E9F7"}
                experityBooking={experityBooking}
                handleSkipBooking={handleSkipBooking}
              />
            )
          ) : (
            <ProviderScheduler
              providers={providerOptions}
              timeSlots={openSlots ?? []}
              isLoading={isCalendarLoading}
              onTimeSlotSelect={handleSubmit}
              leadTimeDays={leadTimeDays}
              timeZone={
                locationOptions.find(
                  (location) =>
                    location.value ===
                    (selectedPracticeId ?? initialPracticeID).toString(),
                )?.timeZone ?? ""
              }
            />
          )}
        </div>
        <div className="flex basis-1 lg:basis-4/12 flex-col gap-8 p-4 lg:p-0">
          <div className="lg:sticky top-24 bottom-24">
            <OrderSummary
              cartItems={cart}
              onSkipBooking={handleSkipBooking}
              orgId={serverData.org.id}
              primaryColor={organizationAssets?.primaryColor ?? "#000000"}
            />
            {selectedAppointmentTime &&
              (() => {
                const practice = allLocations?.find(
                  (p) => p.practiceId === selectedPracticeId,
                );
                return (
                  <BookingDateTimeCard
                    showMap={false}
                    practiceName={practice?.locationName ?? ""}
                    address={practice?.address ?? ""}
                    phoneNumber={practice?.phoneNumber ?? ""}
                    primaryColor={organizationAssets?.primaryColor ?? "#000000"}
                    secondaryColor={
                      organizationAssets?.secondaryColor ?? "#000000"
                    }
                  />
                );
              })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
